// src/pages/Legal/PrivacyPolicyPage.tsx
import { useState } from 'react';
import MainLayout from '../../components/MainLayout/MainLayout';
import { 
  Shield, 
  Eye, 
  Lock, 
  Users, 
  Share2, 
  Database,
  AlertTriangle,
  Check,
  Calendar,
  Mail,
  ChevronRight,
  FileText,
  MapPin,
  Info,
  Star
} from 'lucide-react';

/**
 * PLATFORM STATUS: Company-Owned Content Platform
 * 
 * StrymHub is operated as a company platform where all content is created and produced 
 * by StrymHub and its founder. Privacy policy reflects data collection for viewers only.
 * 
 * FUTURE POSSIBILITY:
 * If invite-only creator partnerships are established, additional privacy terms 
 * for partner creators may be added.
 * 
 * Version: 1.0.0 - Company Platform Model
 */

const PrivacyPolicyPage = () => {
  const [activeSection, setActiveSection] = useState<string>('');

  const tableOfContents = [
    { id: 'overview', title: 'Privacy Overview' },
    { id: 'platform-status', title: 'Platform Status' },
    { id: 'information-collection', title: 'Information We Collect' },
    { id: 'information-usage', title: 'How We Use Your Information' },
    { id: 'information-sharing', title: 'Information Sharing' },
    { id: 'data-security', title: 'Data Security' },
    { id: 'cookies', title: 'Cookies and Tracking' },
    { id: 'user-rights', title: 'Your Rights' },
    { id: 'data-retention', title: 'Data Retention' },
    { id: 'children-privacy', title: 'Children\'s Privacy' },
    { id: 'international', title: 'International Users' },
    { id: 'future-changes', title: 'Future Privacy Updates' },
    { id: 'changes', title: 'Policy Changes' },
    { id: 'contact', title: 'Contact Information' }
  ];

  const dataTypes = [
    {
      icon: Users,
      title: 'Account Information',
      items: ['Full name', 'Email address', 'Phone number', 'Profile picture', 'Account preferences']
    },
    {
      icon: Eye,
      title: 'Usage Information',
      items: ['Content viewed', 'Time spent on platform', 'Search queries', 'Device information', 'IP address']
    },
    {
      icon: Share2,
      title: 'Interaction Data',
      items: ['Comments and likes', 'Content shared', 'Social interactions', 'Communication preferences']
    },
    {
      icon: Database,
      title: 'Technical Data',
      items: ['Browser type', 'Operating system', 'Access times', 'Referring URLs', 'Error logs']
    }
  ];

  const userRights = [
    {
      title: 'Right to Access',
      description: 'Request copies of your personal data we hold'
    },
    {
      title: 'Right to Rectification',
      description: 'Request correction of inaccurate personal data'
    },
    {
      title: 'Right to Erasure',
      description: 'Request deletion of your personal data'
    },
    {
      title: 'Right to Restrict Processing',
      description: 'Request limitation of data processing'
    },
    {
      title: 'Right to Data Portability',
      description: 'Request transfer of your data in a structured format'
    },
    {
      title: 'Right to Object',
      description: 'Object to processing based on legitimate interests'
    }
  ];

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  };

  return (
    <MainLayout>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-orange-50">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-slate-950 via-neutral-900 to-slate-950 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-2xl flex items-center justify-center">
                  <Shield className="h-8 w-8 text-white" />
                </div>
              </div>
              
              <h1 className="text-4xl lg:text-5xl font-bold mb-6">
                Privacy <span className="text-transparent font-display bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 bg-clip-text">Policy</span>
              </h1>
              
              <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
                Your privacy is fundamental to how we operate. This policy explains what data we collect, 
                how we use it, and your rights regarding your personal information. StrymHub is currently 
                in its single-creator phase with simplified data practices.
              </p>
              
              <div className="flex items-center justify-center space-x-4 text-sm text-gray-300">
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-2" />
                  <span>Last updated: December 2025</span>
                </div>
                <div className="flex items-center">
                  <FileText className="h-4 w-4 mr-2" />
                  <span>Version: 1.0.0 (Single Creator)</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="lg:grid lg:grid-cols-4 lg:gap-8">
            {/* Table of Contents */}
            <div className="lg:col-span-1">
              <div className="sticky top-8">
                <div className="bg-white rounded-2xl p-6 shadow-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Table of Contents</h3>
                  <nav className="space-y-1">
                    {tableOfContents.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => scrollToSection(item.id)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors duration-200 flex items-center ${
                          activeSection === item.id
                            ? 'bg-orange-100 text-orange-700 font-medium'
                            : 'text-gray-600 hover:text-orange-600 hover:bg-orange-50'
                        }`}
                      >
                        <ChevronRight className="h-4 w-4 mr-2" />
                        {item.title}
                      </button>
                    ))}
                  </nav>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="lg:col-span-3 mt-8 lg:mt-0">
              <div className="prose prose-lg max-w-none">
                
                {/* Overview Section */}
                <section id="overview" className="mb-12">
                  <div className="bg-white rounded-2xl p-8 shadow-lg">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                      <Shield className="h-6 w-6 text-orange-500 mr-3" />
                      Privacy Overview
                    </h2>
                    <div className="text-gray-700 space-y-4">
                      <p>
                        StrymHub ("we," "our," or "us") is committed to protecting your privacy and ensuring 
                        transparency about how we collect, use, and protect your personal information. This 
                        Privacy Policy applies to all users of the StrymHub platform and services.
                      </p>
                      <div className="bg-orange-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-orange-900 mb-2">Key Principles:</h4>
                        <ul className="space-y-1 text-orange-800">
                          <li className="flex items-center"><Check className="h-4 w-4 mr-2" /> We collect only necessary data</li>
                          <li className="flex items-center"><Check className="h-4 w-4 mr-2" /> We never sell your personal information</li>
                          <li className="flex items-center"><Check className="h-4 w-4 mr-2" /> You control your data and privacy settings</li>
                          <li className="flex items-center"><Check className="h-4 w-4 mr-2" /> We use industry-standard security measures</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Platform Status - Current Single Creator Model */}
                <section id="platform-status" className="mb-12">
                  <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-orange-200">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                      <Star className="h-6 w-6 text-orange-500 mr-3" />
                      Current Platform Status
                    </h2>
                    <div className="text-gray-700 space-y-4">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-start">
                          <Info className="h-5 w-5 text-blue-600 mr-3 mt-0.5" />
                          <div className="text-blue-800">
                            <h4 className="font-semibold mb-1">Company-Owned Platform</h4>
                            <p className="text-sm">
                              StrymHub is a company-owned platform — all content is 100% original, 
                              conceived, filmed, and edited by the founder. We do not currently have payment 
                              processing features, so we do not collect any payment or billing information. 
                              Our privacy practices focus on <strong>viewer data</strong> only.
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div className="border border-green-200 rounded-lg p-4 bg-green-50">
                          <h4 className="font-semibold text-green-900 mb-2">Current Data Collection</h4>
                          <ul className="text-sm text-green-800 space-y-1">
                            <li>• Viewer account information</li>
                            <li>• Viewing preferences and history</li>
                            <li>• Comments and interactions</li>
                            <li>• Basic analytics data</li>
                          </ul>
                        </div>
                        
                        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                          <h4 className="font-semibold text-gray-700 mb-2">Potential Future Data (If Partnerships)</h4>
                          <ul className="text-sm text-gray-600 space-y-1">
                            <li>• Partner payment information</li>
                            <li>• Partner content analytics</li>
                            <li>• Partnership agreement data</li>
                            <li>• Partner verification data</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Information Collection */}
                <section id="information-collection" className="mb-12">
                  <div className="bg-white rounded-2xl p-8 shadow-lg">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Information We Collect</h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      {dataTypes.map((type, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center mb-3">
                            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                              <type.icon className="h-5 w-5 text-orange-600" />
                            </div>
                            <h4 className="text-lg font-semibold text-gray-900 ml-3">{type.title}</h4>
                          </div>
                          <ul className="space-y-1 text-sm text-gray-600">
                            {type.items.map((item, idx) => (
                              <li key={idx} className="flex items-center">
                                <div className="w-1 h-1 bg-orange-500 rounded-full mr-3"></div>
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>

                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <AlertTriangle className="h-5 w-5 text-amber-600 mr-3 mt-0.5" />
                        <div className="text-amber-800">
                          <h4 className="font-semibold mb-1">Automatic Data Collection</h4>
                          <p className="text-sm">
                            Some information is collected automatically when you use our services, including 
                            cookies, device information, and usage analytics to improve your experience.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Information Usage */}
                <section id="information-usage" className="mb-12">
                  <div className="bg-white rounded-2xl p-8 shadow-lg">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">How We Use Your Information</h2>
                    <div className="text-gray-700 space-y-4">
                      <p>We use your personal information for the following purposes:</p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-3">
                          <h4 className="font-semibold text-gray-900">Service Provision</h4>
                          <ul className="space-y-2 text-sm">
                            <li>• Account creation and management</li>
                            <li>• Content delivery and personalization</li>
                            <li>• Customer support and communication</li>
                            <li>• Platform notifications and updates</li>
                          </ul>
                        </div>
                        
                        <div className="space-y-3">
                          <h4 className="font-semibold text-gray-900">Platform Improvement</h4>
                          <ul className="space-y-2 text-sm">
                            <li>• Analytics and usage insights</li>
                            <li>• Feature development and testing</li>
                            <li>• Security monitoring and fraud prevention</li>
                            <li>• Legal compliance and safety</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Information Sharing */}
                <section id="information-sharing" className="mb-12">
                  <div className="bg-white rounded-2xl p-8 shadow-lg">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Information Sharing</h2>
                    <div className="text-gray-700 space-y-4">
                      <p className="font-medium">We do not sell, rent, or trade your personal information. We may share your data only in these limited circumstances:</p>
                      
                      <div className="space-y-4">
                        <div className="border-l-4 border-orange-500 pl-4">
                          <h4 className="font-semibold">Service Providers</h4>
                          <p className="text-sm">Trusted third parties who help us operate our platform (hosting, analytics, email services)</p>
                        </div>
                        
                        <div className="border-l-4 border-blue-500 pl-4">
                          <h4 className="font-semibold">Legal Requirements</h4>
                          <p className="text-sm">When required by law, court order, or government regulation</p>
                        </div>
                        
                        <div className="border-l-4 border-green-500 pl-4">
                          <h4 className="font-semibold">Business Transfers</h4>
                          <p className="text-sm">In the event of a merger, acquisition, or sale of assets</p>
                        </div>
                        
                        <div className="border-l-4 border-red-500 pl-4">
                          <h4 className="font-semibold">Safety and Security</h4>
                          <p className="text-sm">To protect the safety and security of our users and platform</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                {/* User Rights */}
                <section id="user-rights" className="mb-12">
                  <div className="bg-white rounded-2xl p-8 shadow-lg">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Rights</h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      {userRights.map((right, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4 hover:bg-orange-50 transition-colors">
                          <h4 className="font-semibold text-gray-900 mb-2">{right.title}</h4>
                          <p className="text-sm text-gray-600">{right.description}</p>
                        </div>
                      ))}
                    </div>
                    
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-blue-900 mb-2">How to Exercise Your Rights</h4>
                      <p className="text-blue-800 text-sm mb-3">
                        To exercise any of these rights, please contact us at privacy@strymhub.com or through 
                        your account settings. We will respond within 30 days.
                      </p>
                      <div className="flex items-center text-sm text-blue-700">
                        <Mail className="h-4 w-4 mr-2" />
                        <span>Response time: 30 days or less</span>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Data Security */}
                <section id="data-security" className="mb-12">
                  <div className="bg-white rounded-2xl p-8 shadow-lg">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                      <Lock className="h-6 w-6 text-orange-500 mr-3" />
                      Data Security
                    </h2>
                    <div className="text-gray-700 space-y-4">
                      <p>
                        We implement comprehensive security measures to protect your personal information 
                        from unauthorized access, disclosure, alteration, and destruction.
                      </p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="text-center p-4 bg-green-50 rounded-lg">
                          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                            <Lock className="h-6 w-6 text-green-600" />
                          </div>
                          <h4 className="font-semibold text-green-900">Encryption</h4>
                          <p className="text-sm text-green-700 mt-1">Data encrypted in transit and at rest</p>
                        </div>
                        
                        <div className="text-center p-4 bg-blue-50 rounded-lg">
                          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                            <Shield className="h-6 w-6 text-blue-600" />
                          </div>
                          <h4 className="font-semibold text-blue-900">Access Controls</h4>
                          <p className="text-sm text-blue-700 mt-1">Strict access controls and authentication</p>
                        </div>
                        
                        <div className="text-center p-4 bg-orange-50 rounded-lg">
                          <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                            <Eye className="h-6 w-6 text-orange-600" />
                          </div>
                          <h4 className="font-semibold text-orange-900">Monitoring</h4>
                          <p className="text-sm text-orange-700 mt-1">24/7 security monitoring and alerts</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Contact Information */}
                <section id="contact" className="mb-12">
                  <div className="bg-white rounded-2xl p-8 shadow-lg">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Information</h2>
                    <div className="text-gray-700">
                      <p className="mb-4">
                        If you have any questions about this Privacy Policy or our privacy practices, 
                        please contact us:
                      </p>
                      
                      <div className="bg-gray-50 p-6 rounded-lg">
                        <div className="space-y-3">
                          <div className="flex items-center">
                            <Mail className="h-5 w-5 text-orange-500 mr-3" />
                            <span><strong>Privacy Officer:</strong> privacy@strymhub.com</span>
                          </div>
                          <div className="flex items-center">
                            <Mail className="h-5 w-5 text-orange-500 mr-3" />
                            <span><strong>General Contact:</strong> info@strymhub.com</span>
                          </div>
                          <div className="flex items-center">
                            <MapPin className="h-5 w-5 text-orange-500 mr-3" />
                            <span><strong>Address:</strong> London, United Kingdom</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Future Privacy Updates */}
                <section id="future-changes" className="mb-12">
                  <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-blue-200">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                      <Star className="h-6 w-6 text-blue-500 mr-3" />
                      Future Privacy Updates
                    </h2>
                    <div className="text-gray-700 space-y-4">
                      <p>
                        As StrymHub grows, our privacy practices may expand to accommodate new features 
                        and potential invite-only creator partnerships.
                      </p>

                      <div className="bg-gradient-to-r from-blue-50 to-orange-50 border border-blue-200 rounded-lg p-6">
                        <h4 className="font-semibold text-gray-900 mb-3">Potential Future Updates</h4>
                        <p className="text-sm text-gray-700 mb-4">
                          If we establish invite-only creator partnerships, we may update this Privacy Policy to include:
                        </p>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="bg-white/50 rounded-lg p-3">
                            <h5 className="font-semibold text-gray-800 mb-2 text-sm">Partner Data Collection</h5>
                            <ul className="text-xs text-gray-600 space-y-1">
                              <li>• Partnership agreement details</li>
                              <li>• Payment and banking information</li>
                              <li>• Identity verification (if required)</li>
                              <li>• Content analytics and performance data</li>
                            </ul>
                          </div>
                          
                          <div className="bg-white/50 rounded-lg p-3">
                            <h5 className="font-semibold text-gray-800 mb-2 text-sm">Enhanced Features</h5>
                            <ul className="text-xs text-gray-600 space-y-1">
                              <li>• Premium subscription features</li>
                              <li>• Payment processing data</li>
                              <li>• Enhanced viewer preferences</li>
                              <li>• Cross-platform integrations</li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <div className="flex items-start">
                          <AlertTriangle className="h-5 w-5 text-amber-600 mr-3 mt-0.5" />
                          <div className="text-amber-800">
                            <h4 className="font-semibold mb-1">Notification of Changes</h4>
                            <p className="text-sm">
                              All users will be notified via email when significant privacy policy updates 
                              occur. You will have the opportunity to review changes before they take effect 
                              and can choose to delete your account if you disagree with the new terms.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Last Updated */}
                <div className="bg-orange-50 p-6 rounded-lg text-center">
                  <p className="text-orange-700 font-medium">
                    This Privacy Policy was last updated on December 29, 2025
                  </p>
                  <p className="text-orange-600 text-sm mt-2">
                    Version 1.0.0 - Single Creator Model | We will notify users of significant changes via email
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default PrivacyPolicyPage;